import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, UserCircle, Globe } from 'lucide-react'
import useAppStore from '@/store/appStore'
import { useLanguage } from '@/contexts/LanguageContext'
import { Button } from '@/components'
import deepWindLogo from '../../assets/deepwind-logo.png'

const Header: React.FC = () => {
  const { mobileMenuOpen, toggleMobileMenu, setMobileMenuOpen } = useAppStore()
  const { t, language, setLanguage } = useLanguage()
  const location = useLocation()
  const [scrolled, setScrolled] = useState(false)

  const isHomePage = location.pathname === '/'
  const isRobotPage = location.pathname === '/robot'
  const isBlogPage = location.pathname.startsWith('/blog/')
  const isTransparentPage = isHomePage || isRobotPage || isBlogPage

  const navItems = [
    { name: t.nav.home, href: '/' },
    { name: language === 'tr' ? 'Robot' : 'Robot', href: '/robot' },
    { name: t.nav.contact, href: '/contact' },
  ]

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleMobileMenuToggle = (): void => {
    toggleMobileMenu()
  }

  const closeMobileMenu = (): void => {
    setMobileMenuOpen(false)
  }

  const isTransparent = isTransparentPage && !scrolled

  return (
    <header className={`${isTransparentPage ? 'absolute' : 'sticky'} top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isTransparent 
        ? 'bg-transparent border-transparent' 
        : 'bg-white/95 backdrop-blur-md shadow-md border-b border-gray-100'
    }`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <span className={`text-xl font-bold transition-colors duration-300 ${
              isTransparent ? 'text-white drop-shadow-lg' : 'text-secondary-900'
            }`}>
              VentraWind
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-300 ${
                  isTransparent 
                    ? 'text-white hover:text-primary-300 drop-shadow-md' 
                    : 'text-secondary-700 hover:text-primary-600'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Desktop Login and Language Switcher */}
          <div className="hidden md:flex items-center space-x-4">
            <Button
              onClick={() => {
                const newLanguage = language === 'tr' ? 'en' : 'tr'
                setLanguage(newLanguage)
              }}
              variant="outline"
              size="sm"
              className={`transition-all duration-300 min-w-[80px] ${
                isTransparent
                  ? 'bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm'
                  : 'hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600'
              }`}
              title={language === 'tr' ? 'Switch to English' : 'Switch to Turkish'}
            >
              <Globe className="w-4 h-4 mr-2" />
              {language === 'tr' ? 'Türkçe' : 'English'}
            </Button>
            <Link 
              to="/login" 
              className={`flex items-center p-2 rounded-md transition-colors duration-300 ${
                isTransparent
                  ? 'text-white hover:text-primary-300 drop-shadow-md'
                  : 'text-secondary-700 hover:text-primary-600'
              }`}
            >
              <UserCircle className="w-6 h-6" />
              <span className="ml-2 text-sm font-medium">{t.nav.login}</span>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={handleMobileMenuToggle}
              className={`inline-flex items-center justify-center p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset transition-colors duration-300 ${
                isTransparent
                  ? 'text-white hover:bg-white/10 focus:ring-white/50'
                  : 'text-secondary-700 hover:text-primary-600 hover:bg-secondary-100 focus:ring-primary-500'
              }`}
            >
              {mobileMenuOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden">
          <div className={`px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t transition-colors duration-300 ${
            isTransparent 
              ? 'bg-black/90 backdrop-blur-md border-white/10' 
              : 'bg-white border-gray-200'
          }`}>
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                  isTransparent
                    ? 'text-white hover:text-primary-300 hover:bg-white/10'
                    : 'text-secondary-700 hover:text-primary-600 hover:bg-gray-50'
                }`}
                onClick={closeMobileMenu}
              >
                {item.name}
              </Link>
            ))}
            
            <div className={`pt-4 pb-2 border-t space-y-2 ${
              isTransparent ? 'border-white/10' : 'border-secondary-200'
            }`}>
              <div className="flex justify-center">
                <Button
                  onClick={() => {
                    const newLanguage = language === 'tr' ? 'en' : 'tr'
                    setLanguage(newLanguage)
                  }}
                  variant="outline"
                  size="sm"
                  className={`w-full justify-center transition-colors ${
                    isTransparent
                      ? 'bg-white/10 border-white/30 text-white hover:bg-white/20'
                      : 'hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600'
                  }`}
                >
                  <Globe className="w-4 h-4 mr-2" />
                  {language === 'tr' ? 'Türkçe' : 'English'}
                </Button>
              </div>
              <Link 
                to="/login" 
                className={`flex items-center justify-center py-2 rounded-md transition-colors duration-200 ${
                  isTransparent
                    ? 'text-white hover:text-primary-300'
                    : 'text-secondary-700 hover:text-primary-600'
                }`}
              >
                <UserCircle className="w-5 h-5 mr-2" />
                <span className="font-medium">{t.nav.login}</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}

export default Header 